import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MessageSquareHeart } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import { SafetyMenu } from '@/components/SafetyActions';
import { Button, Card, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate, formatRelative } from '@/lib/format';
import type { ChildSummary, HomeMessage } from '@/types';

/**
 * 가정 소식 수신함 (§5.3) — 가정→기관 방향.
 * "가정통신"은 원에서 보내는 가정통신문으로 읽혀 개명했다(v1.10).
 */
export function TalkPage() {
  const queryClient = useQueryClient();

  const messages = useQuery({
    queryKey: ['home-messages'],
    queryFn: () => apiData<(HomeMessage & { userId: string })[]>('/daily-notes/home-messages'),
  });

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      apiData(`/daily-notes/home-messages/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['home-messages'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
    },
  });

  const nameOf = (childId: string) =>
    children.data?.find((child) => child.id === childId)?.name ?? '원아';

  return (
    <Screen>
      <AppBar title="가정 소식" subtitle="가정에서 전해 온 이야기" back />

      {messages.isPending && <Spinner />}
      {messages.isError && (
        <ErrorNote message="이야기를 불러오지 못했어요." onRetry={() => void messages.refetch()} />
      )}
      {messages.data?.length === 0 && (
        <EmptyState
          title="아직 도착한 이야기가 없어요"
          body="가정에서 전달사항을 남기면 여기에 모아 드릴게요."
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {messages.data?.map((message) => (
          <Card key={message.id} as="li" className="flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <MessageSquareHeart size={18} className="mt-1 shrink-0 text-secondary" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{nameOf(message.childId)}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-base">{message.content}</p>
                <p className="mt-1 text-sm text-ink-sub">
                  {formatDate(message.messageDate)} · {formatRelative(message.createdAt)}
                </p>
              </div>
              {/* 안전 도구 (§5.30) — 보호자 신고·차단 */}
              <SafetyMenu
                target={{ type: 'HOME_MESSAGE', id: message.id }}
                user={{ id: message.userId, name: `${nameOf(message.childId)} 보호자` }}
                className="-mr-2 -mt-1"
              />
            </div>

            {message.readAt ? (
              <p className="flex items-center gap-1 text-sm text-primary-strong">
                <Check size={14} /> 확인함
              </p>
            ) : (
              <Button
                size="sm"
                variant="outline"
                loading={markRead.isPending}
                onClick={() => markRead.mutate(message.id)}
              >
                확인 처리
              </Button>
            )}
          </Card>
        ))}
      </ul>
    </Screen>
  );
}
